import { z } from 'zod';
import { FORMULA_MAX_LENGTH, FORMULA_RETURN_TYPES, type FormulaReturnType, resolveFormulaFields } from './formula';
import { type FieldDef, type FieldType, isComputedField, OBJECT_NAMES, type ObjectDef, type ObjectName, OBJECTS, type PicklistOption } from './objects';
import { type Filter, filterSchema } from './validation';

// ---------------------------------------------------------------------------
// Custom fields
// ---------------------------------------------------------------------------

/** Types of stored custom fields. References are not supported. */
export const CUSTOM_FIELD_TYPES = ['text', 'textarea', 'number', 'currency', 'percent', 'date', 'boolean', 'picklist', 'email', 'phone', 'url'] as const satisfies readonly FieldType[];
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
/** Calculated custom fields: never stored, computed whenever a record is read. */
export const COMPUTED_FIELD_TYPES = ['formula', 'rollup'] as const;
/** Lookup to a standard record (account, contact, lead or opportunity). */
export const ALL_CUSTOM_FIELD_TYPES = [...CUSTOM_FIELD_TYPES, 'reference', ...COMPUTED_FIELD_TYPES] as const;
export type CustomFieldKind = (typeof ALL_CUSTOM_FIELD_TYPES)[number];
export const CUSTOM_FIELDS_PER_OBJECT = 100;
/** Roll-ups run a sub-query per record, so they are capped per object. */
export const ROLLUPS_PER_OBJECT = 10;

/** API names of custom fields: `cf_` + lowercase letters, digits and underscores (never clashes with standard camelCase names). */
export function customFieldName(label: string): string {
  const slug = label.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return `cf_${slug || 'field'}`;
}
export const CUSTOM_NAME_RE = /^cf_[a-z0-9_]{1,40}$/;

/** Picklist value keys derived from labels, matching the registry's style. */
export const picklistValueKey = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 60) || 'value';

const optionInput = z.object({ label: z.string().trim().min(1).max(80) });

// --- Roll-up summaries -----------------------------------------------------------

/** A child relationship a roll-up can summarize. Only the standard fields of the child can be used. */
export interface RollupChild {
  key: string;
  label: string;
  table: string;
  /** Column on the child pointing at the parent. */
  fk: string;
  /** Children with soft delete: deleted ones are left out. */
  softDelete: boolean;
  fields: readonly FieldDef[];
}

const LINE_ITEM_FIELDS: FieldDef[] = [
  { name: 'quantity', column: 'quantity', label: 'Quantity', type: 'number' },
  { name: 'unitPrice', column: 'unit_price', label: 'Sales price', type: 'currency' },
  { name: 'discountPct', column: 'discount_pct', label: 'Discount (%)', type: 'percent' },
  { name: 'total', column: 'total', label: 'Total price', type: 'currency' },
];
const standard = (o: ObjectName) => OBJECTS[o].fields.filter((f) => f.type !== 'textarea');

export const ROLLUP_CHILDREN: Record<ObjectName, RollupChild[]> = {
  account: [
    { key: 'opportunities', label: 'Opportunities', table: 'opportunities', fk: 'account_id', softDelete: true, fields: standard('opportunity') },
    { key: 'contacts', label: 'Contacts', table: 'contacts', fk: 'account_id', softDelete: true, fields: standard('contact') },
  ],
  opportunity: [{ key: 'line_items', label: 'Products (line items)', table: 'opportunity_line_items', fk: 'opportunity_id', softDelete: false, fields: LINE_ITEM_FIELDS }],
  contact: [],
  lead: [],
};

export const ROLLUP_FUNCTIONS = ['count', 'sum', 'min', 'max'] as const;
export type RollupFunction = (typeof ROLLUP_FUNCTIONS)[number];

export const rollupDefSchema = z
  .object({
    child: z.string().min(1).max(40),
    fn: z.enum(ROLLUP_FUNCTIONS),
    /** The child field summarized (not for count). */
    field: z.string().min(1).max(64).nullable().optional(),
    /** Only children matching every filter are counted. */
    filters: z.array(filterSchema).max(5).default([]),
  })
  .strict()
  .refine((r) => r.fn === 'count' || !!r.field, { path: ['field'], message: 'Choose the field to summarize' });
export type RollupDef = z.infer<typeof rollupDefSchema>;

/** A roll-up as it appears on a field: the definition plus the parent's table (for the correlated sub-query). */
export interface RollupInfo extends RollupDef { parentTable: string }

/** Problems with a roll-up on `parent`, or the type of the value it produces. */
export function checkRollup(parent: ObjectName, r: RollupDef): { issues: string[]; type: FieldType } {
  const issues: string[] = [];
  const child = ROLLUP_CHILDREN[parent].find((c) => c.key === r.child);
  if (!child) return { issues: [`${OBJECTS[parent].pluralLabel} have no "${r.child}" to summarize`], type: 'number' };
  let type: FieldType = 'number';
  if (r.fn !== 'count') {
    const f = child.fields.find((x) => x.name === r.field);
    if (!f) issues.push(`Unknown field "${r.field ?? ''}" on ${child.label.toLowerCase()}`);
    else if (f.type === 'date' || f.type === 'datetime') {
      if (r.fn === 'sum') issues.push('Dates can only be summarized with minimum or maximum');
      type = 'date';
    } else if (f.type === 'number' || f.type === 'currency' || f.type === 'percent') type = f.type;
    else issues.push(`${f.label} isn't a number or a date`);
  }
  for (const flt of r.filters) if (!child.fields.some((x) => x.name === flt.field)) issues.push(`Unknown filter field "${flt.field}"`);
  return { issues, type };
}

// --- Field definitions -----------------------------------------------------------------

export const formulaDefSchema = z
  .object({ expression: z.string().trim().min(1).max(FORMULA_MAX_LENGTH), returnType: z.enum(FORMULA_RETURN_TYPES) })
  .strict();
export type FormulaDef = z.infer<typeof formulaDefSchema>;

export const customFieldSchema = z
  .object({
    label: z.string().trim().min(1).max(80),
    type: z.enum(ALL_CUSTOM_FIELD_TYPES),
    required: z.boolean().default(false),
    tracked: z.boolean().default(false),
    listDefault: z.boolean().default(false),
    maxLength: z.number().int().min(1).max(32000).optional(),
    /** Picklist only: values in display order. */
    options: z.array(optionInput).max(200).optional(),
    /** Formula fields only. */
    formula: formulaDefSchema.optional(),
    /** Roll-up summary fields only. */
    rollup: rollupDefSchema.optional(),
    /** Reference fields only: the standard object it points at. */
    references: z.enum(OBJECT_NAMES).optional(),
  })
  .strict()
  .refine((v) => v.type !== 'picklist' || (v.options?.length ?? 0) > 0, { path: ['options'], message: 'Add at least one value' })
  .refine((v) => v.type !== 'reference' || !!v.references, { path: ['references'], message: 'Choose what it points to' })
  .refine((v) => v.type !== 'formula' || !!v.formula, { path: ['formula'], message: 'Enter the formula' })
  .refine((v) => v.type !== 'rollup' || !!v.rollup, { path: ['rollup'], message: 'Describe what to summarize' })
  .refine((v) => !(v.type === 'formula' || v.type === 'rollup') || (!v.required && !v.tracked), {
    path: ['required'], message: 'Calculated fields can’t be required or tracked in history',
  });

/** Changing a custom field's type is not allowed (stored values would no longer fit); everything else is. */
export const customFieldUpdateSchema = z
  .object({
    label: z.string().trim().min(1).max(80).optional(),
    required: z.boolean().optional(),
    tracked: z.boolean().optional(),
    listDefault: z.boolean().optional(),
    maxLength: z.number().int().min(1).max(32000).optional(),
    formula: formulaDefSchema.optional(),
    rollup: rollupDefSchema.optional(),
  })
  .strict();

export interface CustomFieldDef {
  id: string;
  /** A standard object, or a custom object's API name (co_…). */
  objectName: string;
  name: string;
  label: string;
  type: CustomFieldKind;
  required: boolean;
  tracked: boolean;
  listDefault: boolean;
  maxLength: number | null;
  sortOrder: number;
  /** Formula, roll-up and reference fields: their definition. */
  definition: { formula?: FormulaDef; rollup?: RollupDef; references?: ObjectName } | null;
}

/** The field definition a custom field contributes to its object (formulas are not yet compiled). */
export function customFieldDef(base: ObjectDef, c: CustomFieldDef): FieldDef {
  const common = { name: c.name, column: 'custom_values', label: c.label, custom: true, listDefault: c.listDefault || undefined } as const;
  if (c.type === 'formula') {
    const d = c.definition?.formula;
    const returnType: FormulaReturnType = d?.returnType ?? 'text';
    return { ...common, type: returnType, readOnly: true, formula: { expression: d?.expression ?? '', returnType } };
  }
  if (c.type === 'reference') {
    return {
      ...common, type: 'reference', references: c.definition?.references ?? 'account',
      required: c.required || undefined, tracked: c.tracked || undefined,
    };
  }
  if (c.type === 'rollup') {
    const d = c.definition?.rollup ?? { child: '', fn: 'count' as const, field: null, filters: [] as Filter[] };
    return { ...common, type: checkRollup(base.name, d).type, readOnly: true, rollup: { ...d, parentTable: base.table } };
  }
  return {
    ...common, type: c.type,
    required: c.required || undefined, tracked: c.tracked || undefined,
    ...(c.maxLength ? { maxLength: c.maxLength } : {}),
  };
}
// ---------------------------------------------------------------------------
// Picklist values (standard and custom picklists)
// ---------------------------------------------------------------------------

export const picklistSaveSchema = z
  .object({
    /** The full list in display order. Omit `value` for a new entry; it's derived from the label. */
    values: z.array(z.object({ value: z.string().max(60).optional(), label: z.string().trim().min(1).max(80), isActive: z.boolean().default(true) }).strict()).min(1).max(500),
  })
  .strict();

/** Values the application relies on: they can be renamed but not deactivated. */
export const SYSTEM_PICKLIST_VALUES: Partial<Record<ObjectName, Record<string, string[]>>> = {
  lead: { status: ['new', 'qualified'] },
};

// ---------------------------------------------------------------------------
// Field-level security
// ---------------------------------------------------------------------------

/** Fields whose access can't be restricted: required fields, the name, the owner and system fields. */
export function isProtectedField(obj: ObjectDef, f: FieldDef): boolean {
  return !!f.required || (f.readOnly && !f.flsReadOnly && !isComputedField(f)) || f.name === 'ownerId' || f.name === obj.nameField || f.name === 'lastName';
}

export type FieldAccessLevel = 'none' | 'read' | 'edit';
export const fieldAccessSaveSchema = z
  .object({ fields: z.array(z.object({ field: z.string().min(1).max(64), access: z.enum(['none', 'read', 'edit']) }).strict()).max(300) })
  .strict();

// ---------------------------------------------------------------------------
// Page layouts
// ---------------------------------------------------------------------------

export const pageLayoutSchema = z
  .object({
    sections: z
      .array(z.object({ title: z.string().trim().min(1).max(80), columns: z.union([z.literal(1), z.literal(2)]).default(2), fields: z.array(z.string().min(1).max(64)).max(200) }).strict())
      .min(1)
      .max(20),
  })
  .strict();
export type PageLayout = z.infer<typeof pageLayoutSchema>;

/** Problems with a layout for an object: unknown fields, duplicates, and required fields left out. */
export function layoutIssues(obj: ObjectDef, layout: PageLayout): string[] {
  const issues: string[] = [];
  const placed = layout.sections.flatMap((s) => s.fields);
  const known = new Set(obj.fields.map((f) => f.name));
  for (const f of placed) if (!known.has(f)) issues.push(`Unknown field "${f}"`);
  if (new Set(placed).size !== placed.length) issues.push('Each field can appear only once');
  for (const f of obj.fields) if (f.required && !f.readOnly && f.type !== 'stage' && f.name !== 'ownerId' && !placed.includes(f.name)) issues.push(`${f.label} is required, so it must be on the layout`);
  return issues;
}

/**
 * Sections to show on a record page or form. Without a layout: one section with every field.
 * Fields the user can't see are dropped; fields added later (not yet placed) are left out, as on a saved layout.
 */
export function layoutSections(obj: ObjectDef, layout: PageLayout | undefined): { title: string; columns: 1 | 2; fields: FieldDef[] }[] {
  const byName = new Map(obj.fields.map((f) => [f.name, f]));
  // Deal Intelligence fields have their own panel on the record page.
  if (!layout) return [{ title: 'Details', columns: 2, fields: obj.fields.filter((f) => f.name !== 'createdAt' && f.name !== 'updatedAt' && !f.intel) }];
  return layout.sections
    .map((s) => ({ title: s.title, columns: s.columns, fields: s.fields.map((n) => byName.get(n)).filter((f): f is FieldDef => !!f) }))
    .filter((s) => s.fields.length > 0);
}

// ---------------------------------------------------------------------------
// The effective schema
// ---------------------------------------------------------------------------

export interface SchemaInputs {
  custom: CustomFieldDef[];
  /** Picklist overrides keyed `${object}.${field}`, in display order. */
  picklists: Map<string, PicklistOption[]>;
  /** Field access per `${object}.${field}`; missing = full access. Undefined = no field-level security (setup views). */
  access?: Map<string, FieldAccessLevel>;
  /** The org has person accounts turned on (otherwise their fields are left out). */
  personAccounts?: boolean;
  /** Objects that have record types: they gain the Record type field. */
  recordTypeObjects?: readonly string[];
}

/** The lookup field an object gains once it has record types (see recordTypes.ts). */
const recordTypeField: FieldDef = { name: 'recordTypeId', column: 'record_type_id', label: 'Record type', type: 'lookup', lookup: 'recordType', tracked: true };

/**
 * Builds one object's definition for an org (and, with `access`, for one user): the registry's
 * fields, then custom fields, with picklist values replaced by the org's own lists. Fields the user
 * may not read are removed; fields they may only read become read-only.
 */
export function effectiveObject(base: ObjectDef, inputs: SchemaInputs): ObjectDef {
  const key = (f: string) => `${base.name}.${f}`;
  const customFields: FieldDef[] = inputs.custom
    .filter((c) => c.objectName === base.name)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => customFieldDef(base, c));
  // Custom fields go before the system timestamps so forms read naturally.
  const sys = base.fields.filter((f) => f.name === 'createdAt' || f.name === 'updatedAt');
  const own = base.fields.filter((x) => !sys.includes(x) && (!x.personAccount || inputs.personAccounts));
  // The record type goes right after the name fields.
  if (inputs.recordTypeObjects?.includes(base.name)) {
    const at = own.findIndex((x) => x.name === 'name' || x.name === 'lastName');
    own.splice(at < 0 ? 0 : at + 1, 0, recordTypeField);
  }
  const all = [...own, ...customFields, ...sys].map((f) => {
    const options = inputs.picklists.get(key(f.picklistSource ?? f.name));
    return (f.type === 'picklist' || f.type === 'multipicklist') && options ? { ...f, options } : f;
  });
  // Formulas are compiled against every field, before field-level security hides any.
  const fields: FieldDef[] = [];
  for (const f of resolveFormulaFields(all)) {
    let next: FieldDef = f;
    const access = inputs.access?.get(key(f.name));
    if (access && !isProtectedField(base, f)) {
      if (access === 'none') continue;
      if (access === 'read' && !f.readOnly) next = { ...next, readOnly: true, flsReadOnly: true };
    }
    fields.push(next);
  }
  return { ...base, fields };
}
